/**
 * Leaguemate Scouting — S3 trade-away signal + full net-affinity (pure).
 *
 * From a manager's COMPLETE trades (§4):
 *   - drops[pid] === their roster_id  → they SENT the player away
 *   - adds[pid]  === their roster_id  → they ACQUIRED the player
 *
 * "Don't-value" = players netted away (sent more than acquired) — easy to pry
 * loose, weak sweeteners to offer them. Net acquisitions = conviction buys that
 * boost the believer score.
 *
 * Full net-affinity (§4):
 *   affinity = w_roster·weightedRosterRate
 *            + w_acq·normalizedNetAcquisitions
 *            − w_away·normalizedTradedAway
 * weights from scoutConfig (provisional).
 */

import { SKILL_POSITIONS, NET_AFFINITY_WEIGHTS, ACQ_BOOST_CAP, AWAY_FLAG_PENALTY } from "./scoutConfig.js";

const SKILL = new Set(SKILL_POSITIONS);
const nameOf = (meta, fallback) => {
  if (!meta) return fallback || null;
  const n = `${meta.first_name || ""} ${meta.last_name || ""}`.trim();
  return n || meta.full_name || fallback || null;
};

/**
 * Aggregate sent/acquired counts per player across the manager's trades.
 * Returns the don't-value list (skill players netted away, value-tagged) plus
 * normalized acquisition / trade-away maps for the net-affinity combine.
 *
 * @param leagues   scoutDeepFetch per-league rows
 * @param playersMap Sleeper players map
 * @param valueMap   FantasyCalc value map
 */
export function computeTradeAway(leagues, playersMap = {}, valueMap = new Map()) {
  const sent = new Map();     // pid -> count sent away
  const acquired = new Map(); // pid -> count acquired
  let tradeCount = 0;

  for (const lg of leagues || []) {
    const rid = lg.roster_id;
    for (const t of lg.completeTx || []) {
      if (t.type !== "trade") continue;
      if (!Array.isArray(t.roster_ids) || !t.roster_ids.includes(rid)) continue;
      tradeCount++;
      for (const [pid, toRid] of Object.entries(t.adds || {})) {
        if (toRid === rid) acquired.set(pid, (acquired.get(pid) || 0) + 1);
      }
      for (const [pid, fromRid] of Object.entries(t.drops || {})) {
        if (fromRid === rid) sent.set(pid, (sent.get(pid) || 0) + 1);
      }
    }
  }

  // Net per player.
  const pids = new Set([...sent.keys(), ...acquired.keys()]);
  const netSentAway = new Map(); // pid -> max(0, sent - acquired)
  const netAcq = new Map();      // pid -> max(0, acquired - sent)
  for (const pid of pids) {
    const s = sent.get(pid) || 0;
    const a = acquired.get(pid) || 0;
    if (s > a) netSentAway.set(pid, s - a);
    if (a > s) netAcq.set(pid, a - s);
  }

  // Normalize to [0,1] per manager (per-league counting already aggregated).
  const maxAway = Math.max(1, ...netSentAway.values());
  const maxAcq = Math.max(1, ...netAcq.values());
  const awayByPlayer = new Map([...netSentAway].map(([pid, n]) => [pid, n / maxAway]));
  const acqByPlayer = new Map([...netAcq].map(([pid, n]) => [pid, n / maxAcq]));

  // Don't-value list (skill players only), value-tagged.
  const dontValue = [];
  for (const [pid, n] of netSentAway) {
    const meta = playersMap[pid];
    const pos = meta?.position || valueMap.get(pid)?.position || null;
    if (!pos || !SKILL.has(pos)) continue;
    const v = valueMap.get(pid) || null;
    dontValue.push({
      player_id: pid,
      name: nameOf(meta, v?.name),
      position: pos,
      value: v?.value ?? null,
      overallRank: v?.overallRank ?? null,
      sent: sent.get(pid) || 0,
      acquired: acquired.get(pid) || 0,
      netSentAway: n,
    });
  }
  // Rank by player VALUE, not away-rate (§4): the most valuable asset he has
  // moved is the actionable tell ("will move a QB"). netSentAway is near-binary
  // at any sample size, so it is not a meaningful sort key.
  dontValue.sort(
    (x, y) =>
      (y.value ?? -1) - (x.value ?? -1) ||
      y.netSentAway - x.netSentAway ||
      String(x.player_id).localeCompare(String(y.player_id))
  );

  return { dontValue, awayByPlayer, acqByPlayer, tradeCount };
}

/**
 * Combine S2 believers with the trade-derived acquisition boost / trade-away
 * penalty into the full net-affinity score, and re-rank.
 */
export function combineNetAffinity(believers, acqByPlayer = new Map(), awayByPlayer = new Map(), weights = NET_AFFINITY_WEIGHTS) {
  const adjusted = (believers || []).map((b) => {
    const netAcqNorm = acqByPlayer.get(b.player_id) || 0;
    // Acquisition boost is CAPPED (§4) so one trade elevates within-tier only.
    const acqBoost = Math.min(weights.acq * netAcqNorm, ACQ_BOOST_CAP);
    // Trade-away is a flat FLAG penalty, not a gradient (§4).
    const tradedAwayFlag = (awayByPlayer.get(b.player_id) || 0) > 0;
    const awayPenalty = tradedAwayFlag ? AWAY_FLAG_PENALTY : 0;
    const adjustedAffinity = weights.roster * b.rate + acqBoost - awayPenalty;
    return {
      ...b,
      netAcqNorm: Math.round(netAcqNorm * 1000) / 1000,
      acqBoost: Math.round(acqBoost * 1000) / 1000,
      tradedAwayFlag,
      adjustedAffinity: Math.round(adjustedAffinity * 1000) / 1000,
    };
  });
  adjusted.sort(
    (x, y) =>
      y.adjustedAffinity - x.adjustedAffinity ||
      (y.value ?? -1) - (x.value ?? -1) ||
      String(x.player_id).localeCompare(String(y.player_id))
  );
  return adjusted;
}
